<template>
  <div v-show="value" class="pv-trend-overlay">
    <div class="pv-trend-panel" @click.stop>
      <div class="drawer-head">
        <div class="drawer-title-wrap">
          <div class="drawer-title">{{ '출력량 추이' | translate }}</div>
          <div class="drawer-sub" :title="subtitle">{{ subtitle }}</div>
        </div>
        <button type="button" class="drawer-close" @click="close">×</button>
      </div>
      <div class="drawer-body">
        <div class="drawer-toolbar">
          <select v-model="trendBucket" class="drawer-bucket" @change="onBucketChange">
            <option value="DAY">{{ '일별' | translate }}</option>
            <option value="WEEK">{{ '주별' | translate }}</option>
            <option value="MONTH">{{ '월별' | translate }}</option>
          </select>
          <div class="drawer-period">{{ periodText }}</div>
        </div>
        <div v-if="loading" class="drawer-empty">{{ '조회 중...' | translate }}</div>
        <div v-else-if="!labels.length" class="drawer-empty">
          {{ '데이터가 없습니다.' | translate }}
        </div>
        <div v-else class="trend-bars">
          <div
            v-for="(label, idx) in labels"
            :key="'pv-trend-' + idx + '-' + label"
            class="trend-bar-item"
            :title="label + ': ' + pagesValues[idx]"
          >
            <div class="bar" :style="{ height: barHeight(pagesValues[idx]) + 'px' }"></div>
            <div class="bar-label">{{ label }}</div>
            <div class="bar-value">{{ pagesValues[idx] }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function toYmd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'PrintVolumeTrendDrawer',
  props: {
    value: { type: Boolean, default: false },
    trendPath: { type: String, default: '' },
    payload: { type: Object, default: () => ({}) },
    titleExtra: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      rows: [],
      trendBucket: 'MONTH',
      requestFrom: '',
      requestTo: '',
      loadQueued: false,
      loadSeq: 0,
    }
  },
  computed: {
    subtitle() {
      return this.titleExtra || ''
    },
    periodText() {
      if (!this.requestFrom && !this.requestTo) return ''
      return `${this.requestFrom} ~ ${this.requestTo}`
    },
    labels() {
      return (this.rows || []).map((r) => r.bucket)
    },
    pagesValues() {
      return (this.rows || []).map((r) => Number(r.pagesA4 || 0))
    },
    maxValue() {
      return Math.max(1, ...this.pagesValues, 1)
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.trendBucket = 'MONTH'
        this.queueLoad()
      }
    },
    payload: {
      deep: true,
      handler() {
        if (this.value) this.queueLoad()
      },
    },
  },
  methods: {
    close() {
      this.$emit('input', false)
    },
    barHeight(v) {
      return Math.max(4, Math.round((Number(v || 0) / this.maxValue) * 160))
    },
    rangeForBucket(bucket) {
      const to = new Date()
      const from = new Date()
      if (bucket === 'MONTH') {
        from.setFullYear(from.getFullYear() - 1)
      } else if (bucket === 'WEEK') {
        from.setDate(from.getDate() - 90)
      } else {
        from.setDate(from.getDate() - 30)
      }
      return { fromDate: toYmd(from), toDate: toYmd(to) }
    },
    onBucketChange() {
      this.queueLoad()
    },
    queueLoad() {
      if (this.loadQueued) return
      this.loadQueued = true
      this.$nextTick(() => {
        this.loadQueued = false
        if (this.value) this.load()
      })
    },
    async load() {
      if (!this.trendPath) return
      const seq = ++this.loadSeq
      const range = this.rangeForBucket(this.trendBucket)
      this.requestFrom = range.fromDate
      this.requestTo = range.toDate
      this.loading = true
      try {
        const res = await this.$request(this.trendPath)
          .setObject({
            ...this.payload,
            fromDate: range.fromDate,
            toDate: range.toDate,
            trendBucket: this.trendBucket,
          })
          .enqueue()
        if (seq !== this.loadSeq) return
        const data = res?.data || res || []
        this.rows = Array.isArray(data) ? data : []
      } catch (e) {
        if (seq !== this.loadSeq) return
        console.error(e)
        this.rows = []
      } finally {
        if (seq === this.loadSeq) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.pv-trend-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(560px, 100%);
  z-index: 40;
  pointer-events: none;
}
.pv-trend-panel {
  pointer-events: auto;
  height: 100%;
  background: #fff;
  box-shadow: -4px 0 16px rgba(15, 23, 42, 0.18);
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 16px 10px;
  border-bottom: 1px solid #e5e7eb;
}
.drawer-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}
.drawer-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.drawer-close {
  border: 0;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
}
.drawer-body {
  flex: 1;
  min-height: 0;
  padding: 16px;
  overflow: auto;
}
.drawer-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.drawer-bucket {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 8px;
  font-size: 12px;
  background: #fff;
}
.drawer-period {
  font-size: 12px;
  color: #475569;
}
.drawer-empty {
  padding: 40px 8px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}
.trend-bars {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  min-height: 200px;
  overflow-x: auto;
  padding-bottom: 8px;
}
.trend-bar-item {
  min-width: 44px;
  text-align: center;
}
.trend-bar-item .bar {
  width: 18px;
  margin: 0 auto 6px;
  background: #249ebf;
  border-radius: 3px 3px 0 0;
}
.trend-bar-item .bar-label {
  font-size: 10px;
  color: #64748b;
  white-space: nowrap;
}
.trend-bar-item .bar-value {
  font-size: 10px;
  color: #334155;
}
</style>
